import React, { useCallback, useEffect, useState } from 'react';
import { Loading, Title, useNotify, useTranslate } from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import {
    ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, Legend, ReferenceLine,
} from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';

import {
    deleteFocusItem, fetchFocusCard, fetchFocusHistory, postFocusCompute, postFocusItem, putFocusCard, putFocusItem, reorderFocusItems,
} from '../../services/api';
import Pill from '../reviews/Pill';
import { CardDialog, ItemDialog } from './FocusDialogs';
import { AlertsBanner, ClosedItems } from './FocusExtras';
import TimesheetCalendar from './TimesheetCalendar';
import { ItemLine, LOAD_COLORS, Meter, QuickAdd, completionColor, initials, qualityColor } from './FocusBoard';

const cardSx = { borderRadius: '12px', height: '100%' };

const Section = ({ title, children, action }) => (
    <Card variant="outlined" sx={cardSx}>
        <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: '8px' }}>
                <Typography variant="h6">{title}</Typography>
                {action}
            </Box>
            {children}
        </CardContent>
    </Card>
);

const Bar100 = ({ label, value }) => {
    const theme = useTheme();
    const color = qualityColor(value);
    const barColor = color === 'default' ? theme.palette.text.disabled : theme.palette[color].main;
    return (
        <Box sx={{ marginTop: '6px' }}>
            <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="caption">{value ?? '-'} %</Typography>
            </Box>
            <LinearProgress variant="determinate" value={value || 0}
                sx={{ height: 5, borderRadius: 3, backgroundColor: alpha(barColor, 0.15), '& .MuiLinearProgress-bar': { backgroundColor: barColor } }} />
        </Box>
    );
};

const Indicators = ({ card }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const details = card.timesheet_quality_details || {};
    // Completion is shown under the measured load, the six quality parts under the quality.
    const quality = card.timesheet_quality;
    const qColor = qualityColor(quality);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2, marginBottom: '16px' }}>
            <Section title={translate('Measured load')}>
                <Meter label={translate('4 weeks')} value={card.load_logged_pct} />
                <Typography variant="body2" sx={{ marginTop: '10px' }}>{card.hours_logged ?? '-'} h / {card.hours_expected ?? '-'} h {translate('due')}</Typography>
                <Typography variant="caption" color="text.secondary">{card.window_start ? `${card.window_start} → ${card.window_end}` : translate('not computed yet')}</Typography>
                {details.coverage_pct !== undefined && (
                    <Box sx={{ marginTop: '12px' }}>
                        <Pill label={`${translate('Completion')} ${details.coverage_pct} %`} color={completionColor(details.coverage_pct)} variant="outlined" />
                        <Typography variant="body2" sx={{ marginTop: '6px' }}>
                            {details.days_logged} / {details.days_due} {translate('days filled')}
                            {details.missing_days ? ` · ${details.missing_days} ${translate('missing')}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('Last line logged')} : {details.last_entry_date || '-'}
                        </Typography>
                    </Box>
                )}
            </Section>
            <Section title={translate('Planned load')}>
                <Meter label={translate('Active priorities')} value={card.load_planned_pct} upper={100} />
                <Typography variant="body2" sx={{ marginTop: '10px' }}>
                    {Number(card.planned_week_days || 0).toLocaleString()} / {Number(card.capacity_days_week).toLocaleString()} {translate('d/wk')}
                </Typography>
            </Section>
            <Section title={translate('Timesheet quality')}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: qColor === 'default' ? 'text.disabled' : theme.palette[qColor].main }}>
                    {quality ?? '-'}{quality !== null && quality !== undefined ? <Typography component="span" variant="body2" color="text.secondary"> /100</Typography> : null}
                </Typography>
                <Bar100 label={`${translate('Precise descriptions')} · 30`} value={details.precise_pct} />
                <Bar100 label={`${translate('Logged the same or next day')} · 25`} value={details.timely_pct} />
                <Bar100 label={`${translate('Days filled')} · 20`} value={details.coverage_pct} />
                <Bar100 label={`${translate('Typed (not other)')} · 10`} value={details.typed_pct} />
                <Bar100 label={`${translate('Lines under 5 h')} · 10`} value={details.short_lines_pct} />
                <Bar100 label={`${translate('Hours not rounded')} · 5`} value={details.unrounded_pct} />
            </Section>
            <Section title={translate('Projects')}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{card.projects_carried ?? '-'}</Typography>
                <Typography variant="body2" color="text.secondary">{translate('carried (8 h or more over 4 weeks)')}</Typography>
                <Typography variant="body2" sx={{ marginTop: '8px' }}>{card.projects_touched ?? '-'} {translate('touched')} · {card.client_projects ?? '-'} {translate('clients')}</Typography>
            </Section>
        </Box>
    );
};

const Priorities = ({ card, onChange, onEdit }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const items = card.items || [];
    const canEdit = card.can_edit;

    const run = async (action) => {
        try {
            await action();
            onChange();
        } catch (error) {
            notify(error.message, { type: 'error' });
        }
    };
    const move = (index, direction) => {
        const ids = items.map(item => item.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + direction, 0, moved);
        run(() => reorderFocusItems(card.user_id, ids));
    };
    const remove = (item) => window.confirm(translate('Delete this priority?')) && run(() => deleteFocusItem(item.id));

    return (
        <Section title={translate('Priorities')}>
            {items.length === 0 && <Typography color="text.disabled">{translate('No priority yet')}</Typography>}
            {items.map((item, index) => (
                <Box key={item.id}>
                    <ItemLine item={item} canEdit={canEdit} detailed first={index === 0} last={index === items.length - 1}
                        onMove={(direction) => move(index, direction)} onEdit={() => onEdit(item)} onDelete={() => remove(item)} />
                    {item.effort_total_days ? (
                        <LinearProgress variant="determinate" value={Math.min(100, 100 * (item.spent_days || 0) / item.effort_total_days)}
                            sx={{ height: 4, borderRadius: 2, marginLeft: '14px', marginBottom: '6px' }} />
                    ) : null}
                </Box>
            ))}
            {canEdit && <QuickAdd onAdd={(data) => run(() => postFocusItem({ user_id: card.user_id, ...data }))} />}
            <ClosedItems
                items={card.closed_items}
                renderItem={(item) => (
                    <ItemLine key={item.id} item={item} canEdit={canEdit} detailed first last onMove={() => {}}
                        onEdit={() => onEdit(item)} onDelete={() => remove(item)} />
                )}
            />
        </Section>
    );
};

const TimeSpent = ({ card }) => {
    const translate = useTranslate();
    return (
        <Section title={translate('Where the time went (4 weeks)')}>
            {(card.top_projects || []).length === 0 && <Typography color="text.disabled">-</Typography>}
            {(card.top_projects || []).map(project => (
                <Box key={project.project_id ?? project.name} sx={{ marginTop: '8px' }}>
                    <Box display="flex" justifyContent="space-between" gap={1}>
                        <Typography variant="body2" noWrap>{project.name}</Typography>
                        <Box display="flex" gap={1} alignItems="center">
                            <Pill label={project.internal ? translate('internal') : translate('client')} color={project.internal ? 'default' : 'primary'} variant="outlined" />
                            <Typography variant="body2" color="text.secondary">{project.hours} h · {project.pct} %</Typography>
                        </Box>
                    </Box>
                    <LinearProgress variant="determinate" value={project.pct} sx={{ height: 5, borderRadius: 3, marginTop: '4px' }} color={project.internal ? 'inherit' : 'primary'} />
                </Box>
            ))}
        </Section>
    );
};

const WeeklyHours = ({ weeks }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const data = (weeks || []).map(week => ({ ...week, label: week.week_start.slice(5) }));
    return (
        <Section title={translate('Hours per week')}>
            <Box sx={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                    <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <ChartTooltip />
                        <Legend />
                        <Bar dataKey="hours_expected" name={translate('Due')} fill={alpha(theme.palette.text.primary, 0.15)} />
                        <Bar dataKey="hours_logged" name={translate('Logged')} fill={theme.palette.primary.main} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Section>
    );
};

const Trend = ({ userId }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const [points, setPoints] = useState(null);

    useEffect(() => {
        let active = true;
        fetchFocusHistory(userId, 90).then(data => active && setPoints(data.results || [])).catch(() => active && setPoints([]));
        return () => {
            active = false;
        };
    }, [userId]);

    return (
        <Section title={translate('Trend, 90 days')}>
            {points && points.length < 2 && (
                <Typography variant="body2" color="text.secondary">{translate('The curve appears after a few daily computations.')}</Typography>
            )}
            {points && points.length >= 2 && (
                <Box sx={{ width: '100%', height: 220 }}>
                    <ResponsiveContainer>
                        <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(value) => value.slice(5)} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <ReferenceLine y={100} stroke={theme.palette.text.disabled} strokeDasharray="3 3" />
                            <ChartTooltip />
                            <Legend />
                            <Line type="monotone" dataKey="load_logged_pct" name={translate('Measured load')} stroke={theme.palette.primary.main} dot={false} strokeWidth={2} />
                            <Line type="monotone" dataKey="load_planned_pct" name={translate('Planned load')} stroke={theme.palette.warning.main} dot={false} />
                            <Line type="monotone" dataKey="timesheet_quality" name={translate('Timesheet quality')} stroke={theme.palette.success.main} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Section>
    );
};

// The full focus of one person. Used as a page (/focus/:userId) and inside the user profile tab.
export const FocusShowContent = ({ userId, embedded = false }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const notify = useNotify();
    const [card, setCard] = useState(null);
    const [cardDialog, setCardDialog] = useState(false);
    const [itemDialog, setItemDialog] = useState(null);
    const [computing, setComputing] = useState(false);

    const load = useCallback(() => {
        fetchFocusCard(userId).then(setCard).catch(error => notify(error.message, { type: 'error' }));
    }, [userId, notify]);

    useEffect(() => {
        load();
    }, [load]);

    if (!card) {
        return <Loading />;
    }

    const run = async (action) => {
        try {
            await action();
            load();
        } catch (error) {
            notify(error.message, { type: 'error' });
        }
    };
    const recompute = async () => {
        setComputing(true);
        await run(() => postFocusCompute(card.user_id));
        setComputing(false);
    };
    const avatarColor = card.user.color || theme.palette.primary.main;

    return (
        <Box sx={{ marginTop: embedded ? 0 : '16px', width: '100%' }}>
            {!embedded && <Title title={`Focus · ${card.user.fullname}`} />}
            {!embedded && (
                <Button component={Link} to="/focus" startIcon={<ArrowBackIcon />} sx={{ marginBottom: '8px' }}>{translate('Back to the board')}</Button>
            )}

            <Card variant="outlined" sx={{ borderRadius: '12px', marginBottom: '16px', borderTop: '4px solid', borderTopColor: avatarColor }}>
                <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Avatar sx={{ width: 64, height: 64, fontSize: 24, bgcolor: avatarColor }}>{initials(card.user)}</Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.user.fullname}</Typography>
                        <Typography color={card.headline ? 'text.primary' : 'text.disabled'}>{card.headline || translate('No headline yet')}</Typography>
                        <Box display="flex" gap={0.75} flexWrap="wrap" sx={{ marginTop: '8px' }}>
                            <Pill label={`${translate('Perceived')} : ${card.manager_load ? translate(`focus_load.${card.manager_load}`, { _: card.manager_load }) : '-'}`}
                                color={LOAD_COLORS[card.manager_load] || 'default'} variant="outlined" />
                            <Pill label={`${translate('Capacity')} : ${Number(card.capacity_days_week).toLocaleString()} ${translate('d/wk')}`} variant="outlined" />
                            {card.computed_at && <Pill label={`${translate('computed on')} ${card.computed_at.slice(0, 10)}`} variant="outlined" />}
                        </Box>
                    </Box>
                    {card.can_edit && (
                        <Box display="flex" gap={1}>
                            <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setCardDialog(true)}>{translate('Settings')}</Button>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={recompute} disabled={computing}>
                                {computing ? translate('Computing…') : translate('Recompute')}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {card.alerts?.length > 0 && <Box sx={{ marginBottom: '16px' }}><AlertsBanner alerts={card.alerts} /></Box>}

            <Indicators card={card} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2, marginBottom: '16px' }}>
                <Priorities card={card} onChange={load} onEdit={setItemDialog} />
                <TimeSpent card={card} />
            </Box>

            <TimesheetCalendar userId={card.user_id} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2, marginBottom: '16px' }}>
                <WeeklyHours weeks={card.weekly_hours} />
                <Trend userId={card.user_id} />
            </Box>

            {(card.ai_summary || card.manager_note) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    {card.ai_summary && (
                        <Section title={translate('Summary')}>
                            <Box display="flex" gap={1}>
                                <AutoAwesomeIcon fontSize="small" color="primary" />
                                <Typography>{card.ai_summary}</Typography>
                            </Box>
                        </Section>
                    )}
                    {card.manager_note && (
                        <Section title={translate('Note')}>
                            <Typography sx={{ whiteSpace: 'pre-line' }}>{card.manager_note}</Typography>
                        </Section>
                    )}
                </Box>
            )}

            {card.can_edit && (
                <>
                    <CardDialog card={card} open={cardDialog} onClose={() => setCardDialog(false)}
                        onSave={(data) => { setCardDialog(false); run(() => putFocusCard(card.user_id, data)); }} />
                    <ItemDialog item={itemDialog} open={Boolean(itemDialog)} suggestions={card.top_projects} onClose={() => setItemDialog(null)}
                        onSave={(data) => { const item = itemDialog; setItemDialog(null); run(() => putFocusItem(item.id, data)); }} />
                </>
            )}
        </Box>
    );
};

const FocusShow = () => {
    const { userId } = useParams();
    return <FocusShowContent userId={userId} />;
};

export default FocusShow;
